import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Modal, Badge } from "react-bootstrap";
import { formatCurrency } from "../../../utils/formatNumber";
import { useConfirmations } from "../api/useConfirmations";
import useColumnSelector from "../../../hooks/useColumnSelector";
import ReactTable from "react-table-6";
import moment from "moment";
import ReactTablePagination from "../../../components/custom/growth/ProductsCVTablePagination";
import { Button } from "../../../components/core/basic/Button";
import Loading from "../../../components/core/blocks/Loading";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import { Link } from "@tanstack/react-router";
import { PRODUCT_AMAZON_LINKS } from "../../../utils/marketplaceConstants";
import { ProductImageURL } from "../../../components/custom/category/listings/ProductImage";
import { cut } from "../../../utils/formatText";
import { DateConfirmedCell } from "./components/DateConfirmedCell";

const ConfirmationSection = ({
  pricingProduct,
  pricingPlan,
  marketPlace,
  date,
  limit = 20,
  priceDiff = "",
  usePricingPlan = false,
  priceMismatch = false,
}) => {
  const { t } = useTranslation(["pricingv2"]);
  const [colToggle] = useState();
  const [verifications, setVerifications] = useState([]);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [search, setSearch] = useState("");

  const tableRef = useRef();

  const {
    isLoading,
    data: confirmations = [],
    error,
  } = useConfirmations({
    marketPlace: marketPlace.marketPlace,
    pricingProductId: pricingProduct?.id,
    planId: pricingPlan?.id,
    date: date ? moment(date).format("YYYY-MM-DD") : undefined,
    priceDiff: priceDiff || undefined,
    priceMismatch: priceMismatch || undefined,
    limit,
  });

  const filteredData =
    search?.length > 0 ?
      confirmations.filter(
        (row) =>
          row.identifiers.asin.toLowerCase().indexOf(search.toLowerCase()) > -1 ||
          row.identifiers.sku.toLowerCase().indexOf(search.toLowerCase()) > -1,
      )
    : null;

  const [columns, columnSelectorProps] = useColumnSelector(() => {
    const columns = [
      {
        id: "SKU",
        label: t(($) => $.pricingv2["SKU"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "left",
            }}
          >
            <span style={{ position: "relative" }}>{t(($) => $.pricingv2["SKU"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "pricing_product",
        width: 400,
        Cell: (props) => {
          return (
            <div className="price-log-grid-container">
              <div
                className="price-log-product-image"
                style={{
                  minWidth: "50px",
                  maxWidth: "50px",
                }}
              >
                <ProductImageURL url={props.original.identifiers.image_url} width="50px" height="50px" />
              </div>
              <div className="price-log-product-title">
                {!usePricingPlan && (
                  <Link
                    to="/user/merchandising/product/$product"
                    params={{ product: String(props.original.identifiers.product_id) }}
                    target="_blank"
                  >
                    {cut(props.original.identifiers.title, 50)}
                  </Link>
                )}
                {usePricingPlan && (
                  <Link
                    to="/user/pricing/plan/$plan"
                    params={{ plan: String(props.original.identifiers.plan_id) }}
                    target="_blank"
                  >
                    {cut(props.original.identifiers.plan_name, 50)}
                  </Link>
                )}
              </div>
              <div className="price-log-product-sku">
                <span>
                  <small>
                    {props.original.identifiers.sku}
                    &nbsp;-&nbsp;
                    <a
                      href={`${PRODUCT_AMAZON_LINKS[marketPlace.marketPlace]}${props.original.identifiers.asin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {props.original.identifiers.asin}
                    </a>
                  </small>
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "DateChanged",
        label: t(($) => $.pricingv2["Date Changed"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <p style={{ position: "relative" }}></p>
            <span style={{ position: "relative", paddingRight: "1rem" }}>{t(($) => $.pricingv2["Date Changed"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "date",
        Cell: (props) => {
          return (
            <div>
              <div className="pb-3">{moment(props.value).format("MMM DD YYYY")}</div>
              <div>&nbsp;</div>
            </div>
          );
        },
      },
      {
        id: "Date Confirmed",
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <span style={{ position: "relative" }}>{t(($) => $.pricingv2["Date"])}</span>
            <span style={{ position: "relative" }}>{t(($) => $.pricingv2["Confirmed"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "confirmation_date",
        Cell: DateConfirmedCell,
      },
      {
        id: "Previous Price",
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <span style={{ position: "relative" }}>{t(($) => $.pricingv2["Previous"])}</span>
            <span style={{ position: "relative" }}>{t(($) => $.pricingv2["Price"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "previous_price",
        Cell: (props) => {
          return props.value > 0 ?
              `${formatCurrency(props.value, {
                marketPlace: marketPlace,
              })}`
            : "";
        },
      },
      {
        id: "New Price",
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <span style={{ position: "relative" }}>{t(($) => $.pricingv2["New"])}</span>
            <span style={{ position: "relative" }}>{t(($) => $.pricingv2["Price"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "new_price",
        Cell: (props) => {
          return props.value > 0 ?
              <span>
                {formatCurrency(props.value, {
                  marketPlace: marketPlace,
                })}

                {props.original.verifications?.length > 0 && (
                  <span className="ps-4">
                    <Badge
                      className={`badge bg-trellis-purple text-end`}
                      style={{
                        borderRadius: "5px",
                      }}
                      type="button"
                      onClick={() => {
                        setShowCheckModal(true);
                        setVerifications(props.original.verifications);
                      }}
                    >
                      {props.original.verifications.length}{" "}
                    </Badge>
                  </span>
                )}
              </span>
            : "";
        },
      },
    ];

    return columns;
  }, [colToggle]);

  if (isLoading) {
    return <Loading fullPage={false} height={`300px`} />;
  }

  if (error) {
    return <DevErrorDisplay error={error} userMessage={t(($) => $.pricingv2["Failed to load confirmations"])} />;
  }

  return (
    <>
      <div>
        <ReactTable
          ref={tableRef}
          data={filteredData ?? confirmations}
          className="pricing-table"
          PaginationComponent={(props) => {
            return (
              <ReactTablePagination
                {...props}
                table={"pricing-table"}
                showFilters={false}
                style={{ marginBottom: "2rem" }}
                carouselLayout={true}
                hideFilters={true}
                updateSearch={setSearch}
                clearSearchTermFilters={() => setSearch("")}
                searchTerm={search}
                columnSelectorProps={columnSelectorProps}
                hideSearch={!!pricingProduct}
              />
            );
          }}
          columns={columns}
          defaultPageSize={20}
          // Below is needed to eliminate the no data message that covers table headers if no data to render
          NoDataComponent={() => null}
          minRows={0}
          showPaginationTop
          showPaginationBottom={false}
          filterable={false}
        />

        {confirmations.length === 0 && (
          <div className="text-center fs-4 pt-5 pb-3">{t(($) => $.pricingv2["No Pricing Plans Found"])}</div>
        )}
      </div>
      <Modal show={showCheckModal} size="lg" onHide={() => setShowCheckModal(false)}>
        <Modal.Header>
          <Row>
            <Col>
              <span className="fs-2">{t(($) => $.pricingv2["Price Verification History"])}</span>
            </Col>
          </Row>
        </Modal.Header>

        <Modal.Body>
          <Row className="px-4">
            <Col className="fs-4 p-3">
              <Row className={`pb-2`}>
                <Col xs={3}>
                  <strong>{t(($) => $.pricingv2["Verification Attempt"])}</strong>
                </Col>
                <Col xs={3} className="text-center">
                  <strong>{t(($) => $.pricingv2["Status"])}</strong>
                </Col>
                <Col xs={3} className="text-end">
                  <strong>{t(($) => $.pricingv2["Expected Price"])}</strong>
                </Col>
                <Col xs={3} className="text-end">
                  <strong>{t(($) => $.pricingv2["Amazon Price"])}</strong>
                </Col>
              </Row>

              {verifications.map((verification, index) => {
                return (
                  <Row key={index} className={`pb-2`}>
                    <Col xs={3}>{moment(verification.date).format("MMM DD YYYY, HH:mm")}</Col>
                    <Col xs={3} className="text-center">
                      {verification.status}
                    </Col>
                    <Col xs={3} className="text-end">
                      {formatCurrency(verification.trellis_price, {
                        marketPlace: marketPlace.marketPlace,
                      })}
                    </Col>
                    <Col xs={3} className="text-end">
                      {formatCurrency(verification.channel_price, {
                        marketPlace: marketPlace.marketPlace,
                      })}
                    </Col>
                  </Row>
                );
              })}
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Row>
            <Col className="text-center">
              <Button onPress={() => setShowCheckModal(false)}>{t(($) => $.pricingv2["Close"])}</Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ConfirmationSection;
